import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { permissionAPI } from '../services/permissionAPI';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

/**
 * Permission Context for managing user permissions and roles
 * Provides centralized permission checking and caching
 */
const PermissionContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  // State management
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Cache for permission checks
  const [permissionCache, setPermissionCache] = useState(new Map());
  const cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * Load user roles and permissions
   */
  const loadUserPermissions = useCallback(async () => {
    if (!user?.id || !isAuthenticated) {
      setUserRoles([]);
      setUserPermissions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if user has elevated role from profile response.
      // Backend now returns RBAC roles in user.role (primary) and user.roles (all).
      const userRole = user.role || 'user';
      const userRolesFromProfile = user.roles || [userRole];
      const isElevatedRole = userRolesFromProfile.some(r => r !== 'user');

      if (!isElevatedRole) {
        // Regular users with only "user" role: no API call needed
        setUserRoles([{ name: userRole, is_active: true }]);
        setUserPermissions([]);
        setLastUpdated(new Date());
        setPermissionCache(new Map());
        logger.info('PermissionContext: Permissions set from profile role', { userId: user.id, role: userRole });
        setLoading(false);
        return;
      }

      logger.info('PermissionContext: Loading admin permissions from API', { userId: user.id });

      // Admin users: load from API with individual error handling
      // so one failing doesn't block the other
      let rolesResponse = [];
      let permissionsResponse = [];

      try {
        rolesResponse = await permissionAPI.getUserRoles(user.id);
      } catch (roleErr) {
        const roleStatus = roleErr.response?.status;
        logger.error('PermissionContext: Failed to load roles from API', {
          userId: user.id,
          jwtRole: userRole,
          status: roleStatus,
          code: roleErr.code,
          message: roleErr.message,
          detail: roleErr.response?.data?.detail,
          hint: roleStatus === 403
            ? 'User JWT role may not match backend permissions — verify role in database'
            : undefined
        });
        rolesResponse = userRolesFromProfile.map(r => ({ name: r, is_active: true }));
      }

      try {
        permissionsResponse = await permissionAPI.getUserPermissions(user.id);
      } catch (permErr) {
        const permStatus = permErr.response?.status;
        logger.error('PermissionContext: Failed to load permissions from API', {
          userId: user.id,
          jwtRole: userRole,
          status: permStatus,
          code: permErr.code,
          message: permErr.message,
          detail: permErr.response?.data?.detail,
          hint: permStatus === 403
            ? 'User JWT role may not match backend permissions — verify role in database'
            : undefined
        });
        permissionsResponse = [];
      }

      setUserRoles(rolesResponse || []);
      setUserPermissions(permissionsResponse || []);
      setLastUpdated(new Date());

      // Clear permission cache when permissions are reloaded
      setPermissionCache(new Map());

      logger.success('PermissionContext: User permissions loaded successfully', {
        userId: user.id,
        rolesCount: rolesResponse?.length || 0,
        permissionsCount: permissionsResponse?.length || 0
      });
    } catch (err) {
      logger.error('PermissionContext: Failed to load user permissions', err);
      setError(err.message || 'Failed to load permissions');
      // Fallback: use JWT role so the app isn't blocked
      const fallbackRole = user.role || 'user';
      setUserRoles([{ name: fallbackRole, is_active: true }]);
      setUserPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.role, user?.roles, isAuthenticated]);

  // Load permissions when user changes
  useEffect(() => {
    loadUserPermissions();
  }, [loadUserPermissions]);

  /**
   * Check if user has a specific permission
   * @param {string} resource - Resource name
   * @param {string} action - Action name
   * @param {boolean} useCache - Whether to use cached results
   * @returns {Promise<boolean>} Whether user has permission
   */
  const hasPermission = useCallback(async (resource, action, useCache = true) => {
    if (!user?.id || !isAuthenticated) {
      return false;
    }

    // Super admin has all permissions
    if (user?.role === 'super_admin') {
      logger.debug('PermissionContext: Super admin bypassed permission check', { resource, action });
      return true;
    }

    const permissionKey = `${resource}:${action}`;
    
    // Check cache first if enabled
    if (useCache && permissionCache.has(permissionKey)) {
      const cached = permissionCache.get(permissionKey);
      if (Date.now() - cached.timestamp < cacheTimeout) {
        logger.debug('PermissionContext: Permission check from cache', { resource, action, result: cached.value });
        return cached.value;
      }
    }

    try {
      logger.debug('PermissionContext: Checking permission via API', { resource, action });
      const result = await permissionAPI.checkPermission(resource, action);
      
      // Cache the result
      setPermissionCache(prev => new Map(prev).set(permissionKey, {
        value: result,
        timestamp: Date.now()
      }));
      
      return result;
    } catch (err) {
      logger.error('PermissionContext: Permission check failed', err);
      return false; // Default to false for security
    }
  }, [user?.id, user?.role, isAuthenticated, permissionCache, cacheTimeout]);

  /**
   * Check multiple permissions at once
   * @param {Array<{resource: string, action: string}>} permissions - Array of permissions to check
   * @returns {Promise<Object>} Object with permission results
   */
  const hasPermissions = useCallback(async (permissions) => {
    if (!user?.id || !isAuthenticated) {
      const results = {};
      permissions.forEach(perm => {
        results[`${perm.resource}:${perm.action}`] = false;
      });
      return results;
    }

    // Super admin has all permissions
    if (user?.role === 'super_admin') {
      const results = {};
      permissions.forEach(perm => {
        results[`${perm.resource}:${perm.action}`] = true;
      });
      logger.debug('PermissionContext: Super admin bypassed bulk permission check', { count: permissions.length });
      return results;
    }

    try {
      logger.debug('PermissionContext: Checking multiple permissions', { count: permissions.length });
      const results = await permissionAPI.checkPermissionsBulk(permissions);
      
      // Cache individual results
      Object.entries(results).forEach(([permKey, hasPermission]) => {
        setPermissionCache(prev => new Map(prev).set(permKey, {
          value: hasPermission,
          timestamp: Date.now()
        }));
      });
      
      return results;
    } catch (err) {
      logger.error('PermissionContext: Bulk permission check failed', err);
      // Return false for all permissions on error
      const results = {};
      permissions.forEach(perm => {
        results[`${perm.resource}:${perm.action}`] = false;
      });
      return results;
    }
  }, [user?.id, user?.role, isAuthenticated]);

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean} Whether user has the role
   */
  const hasRole = useCallback((roleName) => {
    return userRoles.some(role => role.name === roleName && role.is_active !== false);
  }, [userRoles]);

  /**
   * Check if user has any of the specified roles
   * @param {Array<string>} roleNames - Array of role names
   * @returns {boolean} Whether user has any of the roles
   */
  const hasAnyRole = useCallback((roleNames) => {
    return roleNames.some(roleName => hasRole(roleName));
  }, [hasRole]);

  /**
   * Check if user has all of the specified roles
   * @param {Array<string>} roleNames - Array of role names
   * @returns {boolean} Whether user has all roles
   */
  const hasAllRoles = useCallback((roleNames) => {
    return roleNames.every(roleName => hasRole(roleName));
  }, [hasRole]);

  /**
   * Get user's permission for a specific resource
   * @param {string} resource - Resource name
   * @returns {Array<string>} Array of actions user can perform on resource
   */
  const getResourcePermissions = useCallback((resource) => {
    // Super admin has all actions for any resource
    if (user?.role === 'super_admin') {
      return ['create', 'read', 'update', 'delete', 'manage', 'approve', 'block'];
    }
    
    return userPermissions
      .filter(perm => perm.resource === resource && perm.is_active !== false)
      .map(perm => perm.action);
  }, [user?.role, userPermissions]);

  /**
   * Check if user has elevated access (any role beyond basic 'user' or 'guest').
   * This is dynamic — works for admin, super_admin, manager, supervisor,
   * or any future role without hardcoding.
   * @returns {boolean} Whether user has elevated access
   */
  const isAdmin = useMemo(() => {
    const hasElevatedRole = userRoles.some(
      role => role.is_active !== false && role.name && role.name !== 'user' && role.name !== 'guest'
    );
    return hasElevatedRole ||
           userPermissions.some(perm => perm.name === 'system:admin');
  }, [userRoles, userPermissions]);

  /**
   * Check if user is super admin
   * @returns {boolean} Whether user is super admin
   */
  const isSuperAdmin = useMemo(() => {
    return hasRole('super_admin') || 
           userPermissions.some(perm => perm.name === 'system:admin');
  }, [hasRole, userPermissions]);

  /**
   * Refresh user permissions from server
   */
  const refreshPermissions = useCallback(async () => {
    logger.info('PermissionContext: Manually refreshing permissions');
    setPermissionCache(new Map()); // Clear cache
    await loadUserPermissions();
  }, [loadUserPermissions]);

  /**
   * Clear permission cache
   */
  const clearCache = useCallback(() => {
    logger.debug('PermissionContext: Clearing permission cache');
    setPermissionCache(new Map());
    permissionAPI.clearCache();
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    
    for (const [, value] of permissionCache.entries()) {
      if (now - value.timestamp < cacheTimeout) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }
    
    return {
      totalEntries: permissionCache.size,
      validEntries,
      expiredEntries,
      cacheTimeout,
      lastUpdated
    };
  }, [permissionCache, cacheTimeout, lastUpdated]);

  // Clean expired cache entries periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const expiredKeys = [];
      
      for (const [key, value] of permissionCache.entries()) {
        if (now - value.timestamp >= cacheTimeout) {
          expiredKeys.push(key);
        }
      }
      
      if (expiredKeys.length > 0) {
        setPermissionCache(prev => {
          const newCache = new Map(prev);
          expiredKeys.forEach(key => newCache.delete(key));
          return newCache;
        });
        logger.debug('PermissionContext: Cleaned expired cache entries', { count: expiredKeys.length });
      }
    }, 60000); // Check every minute

    return () => clearInterval(cleanupInterval);
  }, [permissionCache, cacheTimeout]);

  const contextValue = useMemo(() => ({
    // State
    userRoles,
    userPermissions,
    loading,
    error,
    lastUpdated,

    // Permission checking methods
    hasPermission,
    hasPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getResourcePermissions,

    // Computed properties
    isAdmin,
    isSuperAdmin,

    // Utility methods
    refreshPermissions,
    clearCache,
    getCacheStats,

    // Permission API access
    permissionAPI
  }), [
    userRoles, userPermissions, loading, error, lastUpdated,
    hasPermission, hasPermissions, hasRole, hasAnyRole, hasAllRoles,
    getResourcePermissions, isAdmin, isSuperAdmin,
    refreshPermissions, clearCache, getCacheStats
  ]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;